import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  Typography,
  Checkbox,
  Button,
  CircularProgress,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Box,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { makeStyles } from '@material-ui/core/styles';
import { appUrl } from '../../../../../../utils/config';
import { MOCK_LOGISTICS } from '../helpers/mockData';

const getApiBase = () =>
  `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}`;

const MONTH_ABBR = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

const useStyles = makeStyles(() => ({
  paper: {
    borderRadius: 12,
    width: 820,
    maxWidth: '96vw',
  },
  content: {
    padding: '0 24px 24px',
    overflowX: 'hidden',
  },
  topRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: 14,
    paddingBottom: 4,
  },
  cancelBtn: {
    textTransform: 'none',
    color: '#333',
    fontWeight: 500,
    fontSize: '0.88rem',
    minWidth: 0,
    padding: '2px 4px',
  },
  title: {
    textAlign: 'center',
    fontWeight: 700,
    fontSize: '1.25rem',
    marginBottom: 18,
    letterSpacing: 0,
  },
  // Header ribbon
  ribbon: {
    display: 'flex',
    gap: 80,
    backgroundColor: '#EEF2F7',
    borderRadius: 8,
    padding: '16px 24px',
    marginBottom: 16,
  },
  ribbonItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  ribbonLabel: {
    fontWeight: 700,
    fontSize: '0.875rem',
    color: '#222',
    lineHeight: 1.3,
  },
  ribbonValue: {
    fontWeight: 400,
    fontSize: '0.875rem',
    color: '#222',
    lineHeight: 1.3,
  },
  tableWrap: {
    maxHeight: 260,
    overflowY: 'auto',
    border: '1px solid #E0E0E0',
    borderRadius: 4,
  },
  headCell: {
    backgroundColor: '#F9F9FC',
    fontWeight: 700,
    padding: '8px 10px',
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
    lineHeight: 1.3,
    borderBottom: '1px solid #E0E0E0',
  },
  checkHeadCell: {
    backgroundColor: '#F9F9FC',
    padding: '8px 6px',
    width: 36,
    borderBottom: '1px solid #E0E0E0',
  },
  cell: {
    padding: '7px 10px',
    fontSize: '0.825rem',
    borderBottom: '1px solid #F0F0F0',
    lineHeight: 1.4,
  },
  checkCell: {
    padding: '5px 6px',
    width: 36,
    borderBottom: '1px solid #F0F0F0',
  },
  orangeCheckbox: {
    color: '#E75B25',
    '&.Mui-checked': { color: '#E75B25' },
    padding: 4,
  },
  bottomBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 4px 6px',
    borderTop: '1px solid #F0F0F0',
    marginTop: 4,
    flexWrap: 'wrap',
    gap: 8,
  },
  alsoWrap: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 2,
  },
  alsoLabel: {
    fontSize: '0.875rem',
    fontWeight: 600,
    marginRight: 6,
    color: '#111',
  },
  alsoCheckLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: '0.825rem',
    },
    marginRight: 6,
  },
  alsoValue: {
    color: '#555',
    marginLeft: 3,
  },
  addCommentBtn: {
    textTransform: 'none',
    border: '1px solid #555',
    borderRadius: 18,
    padding: '5px 18px',
    fontSize: '0.825rem',
    fontWeight: 500,
  },
  submitWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 16,
  },
  submitBtn: {
    textTransform: 'none',
    borderRadius: 22,
    backgroundColor: '#E75B25',
    color: '#fff',
    padding: '8px 28px',
    fontSize: '0.88rem',
    '&:hover': { backgroundColor: '#C44A1A' },
    '&.Mui-disabled': { backgroundColor: '#E0A080', color: '#fff' },
  },
  commentLabel: {
    fontWeight: 600,
    fontSize: '0.925rem',
    marginBottom: 8,
    color: '#111',
  },
  errorMsg: {
    color: '#A02828',
    fontSize: '0.82rem',
    textAlign: 'center',
    marginTop: 8,
  },
  // success dialog
  successPaper: {
    borderRadius: 12,
    padding: '36px 52px',
    textAlign: 'center',
    minWidth: 320,
  },
  successIcon: {
    color: '#4CAF50',
    fontSize: 64,
    marginBottom: 12,
    display: 'block',
    margin: '0 auto 12px',
  },
  successTitle: {
    fontWeight: 700,
    fontSize: '1.05rem',
    marginBottom: 22,
    lineHeight: 1.5,
  },
  okBtn: {
    textTransform: 'none',
    borderRadius: 22,
    backgroundColor: '#E75B25',
    color: '#fff',
    padding: '6px 36px',
    fontSize: '0.88rem',
    '&:hover': { backgroundColor: '#C44A1A' },
  },
}));

const GenerateProformaDialog = ({ open, row, onClose }) => {
  const classes = useStyles();

  const [step, setStep] = useState('main');
  const [batches, setBatches] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [checkedBatches, setCheckedBatches] = useState(new Set());
  const [includes, setIncludes] = useState({
    sponsorShipmentID: true,
    freightForwarderID: true,
    cmoShipmentID: true,
  });
  const [commentText, setCommentText] = useState('');
  const [savedComment, setSavedComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep('main');
      setBatches([]);
      setFetchError(false);
      setCheckedBatches(new Set());
      setIncludes({ sponsorShipmentID: true, freightForwarderID: true, cmoShipmentID: true });
      setCommentText('');
      setSavedComment('');
      setSubmitting(false);
      setSubmitError(false);
      setSubmitSuccess(false);
      return;
    }
    fetchBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row]);

  const fetchBatches = async () => {
    if (MOCK_LOGISTICS) {
      const list = (row?.batchesList || []).map((b) => ({
        ...b,
        batchUOM: b.batchUOM || b.dispatchUOM,
      }));
      setBatches(list);
      setCheckedBatches(new Set(list.map((_, i) => i)));
      return;
    }
    setFetchLoading(true);
    setFetchError(false);
    try {
      const res = await axios.post(`${getApiBase()}/fetchBatchesForShipmentIDGeneration`, {
        aslNumber: null,
        temperature: null,
        deliveryAddresseeOrgName: null,
        sponsorShipmentID: row?.sponsorShipmentID || null,
      });
      const list = res.data.data.batchesList || [];
      setBatches(list);
      setCheckedBatches(new Set(list.map((_, i) => i)));
    } catch (err) {
      console.error('[GenerateProforma] Failed to fetch batches', err);
      setFetchError(true);
    } finally {
      setFetchLoading(false);
    }
  };

  const toggleBatch = (idx) => {
    setCheckedBatches((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleSaveComment = () => {
    setSavedComment(commentText);
    setStep('main');
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(false);
    try {
      const selectedBatches = batches.filter((_, i) => checkedBatches.has(i));
      const requestPayload = {
        collabrixFGBatchShipmentID: row?.collabrixFGBatchShipmentID || null,
        sponsorShipmentID: row?.sponsorShipmentID || null,
        proformaMetadata: {
          deliveryDate: row?.actualDispatchDate
            ? new Date(row.actualDispatchDate).toISOString().split('T')[0]
            : null,
          items: selectedBatches.map((b) => ({
            itemNumber: (b.scheduleLineItemNumber || '').padStart(6, '0'),
            fgMaterialCode: (b.fgMaterialCode || '').padStart(18, '0'),
            plant: b.vendorPlant || '',
            storageLocation: b.storageLocation || '',
            fgBatch: b.vendorBatchNumber || '',
            fgBatchQuantity: b.dispatchQuantity != null
              ? Number(b.dispatchQuantity).toFixed(3)
              : '0.000',
            quantityUOM: b.batchUOM || b.dispatchUOM || '',
          })),
        },
      };
      // Timeout guard: the backend fans out to a SAP service + Temporal that may
      // be unreachable in this environment. Without a timeout the request hangs
      // and the "Generating Proforma…" overlay never clears (the UI "freezes").
      await axios.post(`${getApiBase()}/generateProforma`, requestPayload, { timeout: 20000 });
      setSubmitSuccess(true);
    } catch (err) {
      console.error('[GenerateProforma] Submit failed', err);
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <Dialog open={open} classes={{ paper: classes.successPaper }}>
        <CheckCircleIcon className={classes.successIcon} />
        <Typography className={classes.successTitle}>
          Information for Proforma Invoice<br />Generation Provided
        </Typography>
        <Button className={classes.okBtn} onClick={onClose}>
          Ok
        </Button>
      </Dialog>
    );
  }

  const metaFields = [
    { label: 'CMO',                  value: row?.vendorName                 || '' },
    { label: 'Sponsor Shipment ID',  value: row?.sponsorShipmentID          || '' },
    { label: 'FF Schedule ID',       value: row?.freightForwarderScheduleID || '' },
    { label: 'Dispatch Date',        value: row?.actualDispatchDate ? formatDate(row?.actualDispatchDate) : ""    },
  ];

  const alsoIncludeOptions = [
    { key: 'sponsorShipmentID',  label: 'Sponsor Shipment ID',  value: row?.sponsorShipmentID          || '' },
    { key: 'freightForwarderID', label: 'Freight Forwarder ID', value: row?.freightForwarderScheduleID || '' },
    { key: 'cmoShipmentID',      label: 'CMO Shipment ID',      value: 'CMO-DUMMY'                           },
  ];

  return (
    <>
      {submitting && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.45)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          gap: 16,
        }}>
          <CircularProgress size={52} style={{ color: '#fff' }} />
          <Typography style={{ color: '#fff', fontSize: '1rem', fontWeight: 500 }}>
            Generating Proforma…
          </Typography>
        </div>
      )}
      <Dialog open={open} classes={{ paper: classes.paper }} maxWidth={false}>
      <DialogContent className={classes.content}>

        {/* Cancel */}
        <div className={classes.topRow}>
          <Button
            className={classes.cancelBtn}
            endIcon={<CloseIcon style={{ color: '#E75B25', fontSize: 18 }} />}
            onClick={onClose}
          >
            Cancel
          </Button>
        </div>

        {/* Title */}
        <Typography className={classes.title}>
          Information for Generating Proforma Invoice
        </Typography>

        {/* Header ribbon */}
        <div className={classes.ribbon}>
          {metaFields.map(({ label, value }) => (
            <div key={label} className={classes.ribbonItem}>
              <Typography className={classes.ribbonLabel}>{label}</Typography>
              <Typography className={classes.ribbonValue}>{value}</Typography>
            </div>
          ))}
        </div>

        {step === 'comment' ? (
          <>
            <Typography className={classes.commentLabel}>Comment</Typography>
            <TextField
              fullWidth
              multiline
              minRows={6}
              variant="outlined"
              placeholder="Drop Shipment"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
            />
            <div className={classes.submitWrap}>
              <Button className={classes.submitBtn} onClick={handleSaveComment}>
                Add Comment
              </Button>
            </div>
          </>
        ) : (
          <>
            {fetchLoading ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <>
                <TableContainer className={classes.tableWrap}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.checkHeadCell} />
                        <TableCell className={classes.headCell}>PO Number</TableCell>
                        <TableCell className={classes.headCell}>
                          Product Code /<br />Description
                        </TableCell>
                        <TableCell className={classes.headCell}>
                          Batch<br />Number
                        </TableCell>
                        <TableCell className={classes.headCell} align="right">Quantity</TableCell>
                        <TableCell className={classes.headCell}>UOM</TableCell>
                        <TableCell className={classes.headCell}>
                          Manufacturing<br />Date
                        </TableCell>
                        <TableCell className={classes.headCell}>
                          Expiry<br />Date
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fetchError ? (
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            align="center"
                            style={{ padding: 24, color: '#d32f2f', fontSize: '0.8rem' }}
                          >
                            Something went wrong. Please try again.
                          </TableCell>
                        </TableRow>
                      ) : batches.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            align="center"
                            style={{ padding: 24, color: '#9E9E9E', fontSize: '0.8rem' }}
                          >
                            No batch data available.
                          </TableCell>
                        </TableRow>
                      ) : (
                        batches.map((b, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell className={classes.checkCell}>
                              <Checkbox
                                className={classes.orangeCheckbox}
                                checked={checkedBatches.has(idx)}
                                onChange={() => toggleBatch(idx)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell className={classes.cell}>
                              {b.scheduleAgreementNumber || ''}
                            </TableCell>
                            <TableCell className={classes.cell}>
                              {b.fgMaterialCode && b.fgMaterialName ? <>{b.fgMaterialCode}
                              <br />
                              {b.fgMaterialName}</> : "" }
                              
                            </TableCell>
                            <TableCell className={classes.cell}>
                              {b.vendorBatchNumber || ''}
                            </TableCell>
                            <TableCell className={classes.cell} align="right">
                              {b.dispatchQuantity != null
                                ? Number(b.dispatchQuantity).toLocaleString()
                                : ''}
                            </TableCell>
                            <TableCell className={classes.cell}>{b.batchUOM || ''}</TableCell>
                            <TableCell className={classes.cell}>
                              {b.manufacturingDate ? formatDate(b.manufacturingDate): ""}
                            </TableCell>
                            <TableCell className={classes.cell}>
                              {b.expiryDate ? formatDate(b.expiryDate): ""}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Also include + Add Comment */}
                <div className={classes.bottomBar}>
                  <div className={classes.alsoWrap}>
                    <Typography className={classes.alsoLabel}>Also include:</Typography>
                    {alsoIncludeOptions.map(({ key, label, value }) => (
                      <FormControlLabel
                        key={key}
                        className={classes.alsoCheckLabel}
                        control={
                          <Checkbox
                            className={classes.orangeCheckbox}
                            checked={includes[key]}
                            onChange={() =>
                              setIncludes((prev) => ({ ...prev, [key]: !prev[key] }))
                            }
                            size="small"
                          />
                        }
                        label={
                          <span>
                            {label}
                          </span>
                        }
                      />
                    ))}
                  </div>
                  <Button
                    variant="outlined"
                    className={classes.addCommentBtn}
                    onClick={() => {
                      setCommentText(savedComment);
                      setStep('comment');
                    }}
                  >
                    {savedComment ? 'Edit Comment' : 'Add Comment'}
                  </Button>
                </div>

                {submitError && (
                  <Typography className={classes.errorMsg}>
                    Something went wrong. Try again.
                  </Typography>
                )}

                <div className={classes.submitWrap}>
                  <Button
                    className={classes.submitBtn}
                    onClick={handleSubmit}
                    disabled={submitting || checkedBatches.size === 0}
                  >
                    {submitting ? (
                      <CircularProgress size={18} style={{ color: '#fff' }} />
                    ) : (
                      'Provide Information for Proforma Generation'
                    )}
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default GenerateProformaDialog;
